import { font, type FontPrefix } from './font';
import { MetaTokenProperties } from './types';

export type TextVariant =
    | 'heading-3xl'
    // 36px money figure — a guide's computed figure, the one number the page is built around
    // (pv-frontend#358). A step above figure-lg because it leads a block rather than a card:
    // the reader is meant to stop on it, and at hero size it competes with the cover instead.
    | 'figure-xl'
    // 28px money figure — the ownership hero's equity total, and the portfolio total beside it.
    // Its own variant rather than a heading step: it exists to be read as a number, so it takes
    // the tightest tracking on the scale and a line height sized to a single line of digits.
    | 'figure-lg'
    | 'heading-2xl'
    | 'heading-xl'
    // 20px pushed-screen title — the one title size on `ScreenHeader` (pv-frontend#667). Fills
    // the 18→22 gap (size-500) the approved header `.pen` frame states as 20/700, and which no
    // variant exposed: `heading-xl` is 22 and `title-lg` is 18, so every header before this one
    // either overshot the frame or used `heading-lg` at 16 and read as a section head.
    | 'title-xl'
    // 18px lead heading — the coached-home hero next-step. Fills the 16→22 gap
    // (size-450) the .pen uses via `$size-450` but no variant exposed.
    | 'title-lg'
    | 'heading-lg'
    | 'heading-md'
    | 'heading-sm'
    // | 'heading-xs'
    | 'body-lg'
    | 'body-md'
    | 'body-sm'
    | 'body-xs';

export type TextTokenName = `text-${TextVariant}-${FontPrefix}`;

export type TextTokenGroup = {
    [TokenName in TextTokenName]: string;
};

export const text: {
    [TokenName in TextTokenName]: MetaTokenProperties;
} = {
    // heading-3xl
    'text-heading-3xl-font-family': {
        value: font['font-family-sans'].value,
    },
    'text-heading-3xl-font-size': {
        value: font['font-size-750'].value,
        unit: 'px',
    },
    'text-heading-3xl-font-weight': {
        value: font['font-weight-bold'].value,
    },
    'text-heading-3xl-font-letter-spacing': {
        value: font['font-letter-spacing-densest'].value,
        unit: 'px',
    },
    'text-heading-3xl-font-line-height': {
        value: font['font-line-height-800'].value,
        unit: 'px',
    },
    // figure-xl — 36px ($size-900), the computed figure a guide stops on.
    'text-figure-xl-font-family': {
        value: font['font-family-sans'].value,
    },
    'text-figure-xl-font-size': {
        value: font['font-size-900'].value,
        unit: 'px',
    },
    'text-figure-xl-font-weight': {
        value: font['font-weight-bold'].value,
    },
    'text-figure-xl-font-letter-spacing': {
        value: font['font-letter-spacing-densest'].value,
        unit: 'px',
    },
    'text-figure-xl-font-line-height': {
        value: font['font-line-height-1000'].value,
        unit: 'px',
    },
    // figure-lg — 28px ($size-700), the big money figure a hero leads with.
    'text-figure-lg-font-family': {
        value: font['font-family-sans'].value,
    },
    'text-figure-lg-font-size': {
        value: font['font-size-700'].value,
        unit: 'px',
    },
    'text-figure-lg-font-weight': {
        value: font['font-weight-bold'].value,
    },
    'text-figure-lg-font-letter-spacing': {
        value: font['font-letter-spacing-densest'].value,
        unit: 'px',
    },
    'text-figure-lg-font-line-height': {
        value: font['font-line-height-800'].value,
        unit: 'px',
    },
    // heading-2xl
    'text-heading-2xl-font-family': {
        value: font['font-family-sans'].value
    },
    'text-heading-2xl-font-size': {
        value: font['font-size-600'].value,
        unit: 'px'
    },
    'text-heading-2xl-font-weight': {
        value: font['font-weight-bold'].value
    },
    'text-heading-2xl-font-letter-spacing': {
        value: font['font-letter-spacing-dense'].value,
        unit: 'px'
    },
    'text-heading-2xl-font-line-height': {
        value: font['font-line-height-800'].value,
        unit: 'px'
    },
    // heading-xl
    'text-heading-xl-font-family': {
        value: font['font-family-sans'].value
    },
    'text-heading-xl-font-size': {
        value: font['font-size-550'].value,
        unit: 'px'
    },
    'text-heading-xl-font-weight': {
        value: font['font-weight-bold'].value
    },
    'text-heading-xl-font-letter-spacing': {
        value: font['font-letter-spacing-dense'].value,
        unit: 'px'
    },
    'text-heading-xl-font-line-height': {
        value: font['font-line-height-700'].value,
        unit: 'px'
    },
    // title-xl — 20px pushed-screen title (ScreenHeader); maps to $size-500, line box $size-600.
    'text-title-xl-font-family': {
        value: font['font-family-sans'].value
    },
    'text-title-xl-font-size': {
        value: font['font-size-500'].value,
        unit: 'px'
    },
    'text-title-xl-font-weight': {
        value: font['font-weight-bold'].value
    },
    'text-title-xl-font-letter-spacing': {
        value: font['font-letter-spacing-dense'].value,
        unit: 'px'
    },
    'text-title-xl-font-line-height': {
        value: font['font-line-height-600'].value,
        unit: 'px'
    },
    // title-lg — 18px lead heading (coached-home hero next-step); maps to $size-450.
    'text-title-lg-font-family': {
        value: font['font-family-sans'].value
    },
    'text-title-lg-font-size': {
        value: font['font-size-450'].value,
        unit: 'px'
    },
    'text-title-lg-font-weight': {
        value: font['font-weight-bold'].value
    },
    'text-title-lg-font-letter-spacing': {
        value: font['font-letter-spacing-dense'].value,
        unit: 'px'
    },
    'text-title-lg-font-line-height': {
        value: font['font-line-height-500'].value,
        unit: 'px'
    },
    // heading-lg
    'text-heading-lg-font-family': {
        value: font['font-family-sans'].value
    },
    'text-heading-lg-font-size': {
        value: font['font-size-400'].value,
        unit: 'px'
    },
    'text-heading-lg-font-weight': {
        value: font['font-weight-semibold'].value
    },
    'text-heading-lg-font-letter-spacing': {
        value: font['font-letter-spacing-dense'].value,
        unit: 'px'
    },
    'text-heading-lg-font-line-height': {
        value: font['font-line-height-400'].value,
        unit: 'px'
    },
    // heading-md
    'text-heading-md-font-family': {
        value: font['font-family-sans'].value
    },
    'text-heading-md-font-size': {
        value: font['font-size-350'].value,
        unit: 'px'
    },
    'text-heading-md-font-weight': {
        value: font['font-weight-semibold'].value
    },
    'text-heading-md-font-letter-spacing': {
        value: font['font-letter-spacing-normal'].value,
        unit: 'px'
    },
    'text-heading-md-font-line-height': {
        value: font['font-line-height-500'].value,
        unit: 'px'
    },
    // heading-sm
    'text-heading-sm-font-family': {
        value: font['font-family-sans'].value
    },
    'text-heading-sm-font-size': {
        value: font['font-size-325'].value,
        unit: 'px'
    },
    'text-heading-sm-font-weight': {
        value: font['font-weight-semibold'].value
    },
    'text-heading-sm-font-letter-spacing': {
        value: font['font-letter-spacing-normal'].value,
        unit: 'px'
    },
    'text-heading-sm-font-line-height': {
        value: font['font-line-height-500'].value,
        unit: 'px'
    },
    // // heading-xs
    // 'text-heading-xs-font-family': {
    //     value: font['font-family-sans'].value,
    // },
    // 'text-heading-xs-font-size': {
    //     value: font['font-size-300'].value,
    //     unit: 'px'
    // },
    // 'text-heading-xs-font-weight': {
    //     value: font['font-weight-semibold'].value,
    // },
    // 'text-heading-xs-font-letter-spacing': {
    //     value: font['font-letter-spacing-normal'].value,
    //     unit: 'px'
    // },
    // 'text-heading-xs-font-line-height': {
    //     value: font['font-line-height-400'].value,
    //     unit: 'px'
    // },
    // body-lg
    'text-body-lg-font-family': {
        value: font['font-family-sans'].value,
    },
    'text-body-lg-font-size': {
        value: font['font-size-350'].value,
        unit: 'px'
    },
    'text-body-lg-font-weight': {
        value: font['font-weight-regular'].value,
    },
    'text-body-lg-font-letter-spacing': {
        value: font['font-letter-spacing-normal'].value,
        unit: 'px'
    },
    'text-body-lg-font-line-height': {
        value: font['font-line-height-500'].value,
        unit: 'px'
    },
    // body-md
    'text-body-md-font-family': {
        value: font['font-family-sans'].value,
    },
    'text-body-md-font-size': {
        value: font['font-size-325'].value,
        unit: 'px'
    },
    'text-body-md-font-weight': {
        value: font['font-weight-regular'].value,
    },
    'text-body-md-font-letter-spacing': {
        value: font['font-letter-spacing-normal'].value,
        unit: 'px'
    },
    'text-body-md-font-line-height': {
        value: font['font-line-height-500'].value,
        unit: 'px'
    },
    // body-sm
    'text-body-sm-font-family': {
        value: font['font-family-sans'].value,
    },
    'text-body-sm-font-size': {
        value: font['font-size-300'].value,
        unit: 'px'
    },
    'text-body-sm-font-weight': {
        value: font['font-weight-regular'].value,
    },
    'text-body-sm-font-letter-spacing': {
        value: font['font-letter-spacing-normal'].value,
        unit: 'px'
    },
    'text-body-sm-font-line-height': {
        value: font['font-line-height-400'].value,
        unit: 'px'
    },
    // body-xs — 11px (size-275); the hero stage line + header kicker.
    'text-body-xs-font-family': {
        value: font['font-family-sans'].value,
    },
    'text-body-xs-font-size': {
        value: font['font-size-275'].value,
        unit: 'px'
    },
    'text-body-xs-font-weight': {
        value: font['font-weight-regular'].value,
    },
    'text-body-xs-font-letter-spacing': {
        value: font['font-letter-spacing-normal'].value,
        unit: 'px'
    },
    'text-body-xs-font-line-height': {
        value: font['font-line-height-300'].value,
        unit: 'px'
    },
};